import React from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import { fullName } from 'utils/triage';
import { Person } from '../escalationContacts';

export const FIELD_LABEL_SX = {
  flex: 'none',
  width: '64px',
  paddingTop: '9px',
  fontSize: '14px',
  color: '#7D818C',
};

// One row of an email-style address block: the label on the left, removable
// chips and a search box on the right.
export const FieldRow: React.FC<{ label: string; divider?: boolean }> = ({
  label,
  divider,
  children,
}) => (
  <Stack
    direction="row"
    alignItems="flex-start"
    sx={{
      paddingX: '12px',
      paddingY: '4px',
      minHeight: '44px',
      borderTop: divider ? '1px solid #EFEDEB' : undefined,
    }}
  >
    <Typography sx={FIELD_LABEL_SX} aria-hidden>
      {label}
    </Typography>
    <Stack sx={{ flex: 1, minWidth: 0 }}>{children}</Stack>
  </Stack>
);

const ADD_PLACEHOLDER = 'Add name or email';

const CHIP_SX = {
  backgroundColor: '#EFEDEB',
  color: '#213B54',
  fontSize: '13px',
  fontWeight: 500,
};

// Name and email in separate spans so the name stays findable on its own; a
// person with no email, or whose name falls back to the email, gets no "()".
const PersonChip: React.FC<
  { person: Person } & React.ComponentProps<typeof Chip>
> = ({ person, ...chipProps }) => {
  const name = fullName(person);
  const showEmail = !!person.email && person.email !== name;
  return (
    <Chip
      {...chipProps}
      size="small"
      title={showEmail ? `${name} (${person.email})` : name}
      label={
        showEmail ? (
          <>
            <span>{name}</span>
            <Box component="span" sx={{ color: '#7D818C' }}>
              {` (${person.email})`}
            </Box>
          </>
        ) : (
          name
        )
      }
      sx={CHIP_SX}
    />
  );
};

interface IRecipientFieldProps {
  label: string;
  people: Person[];
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Shown while nobody is chosen; once someone is, the add prompt shows. */
  placeholder?: string;
  noOptionsText?: string;
  divider?: boolean;
  /** The contacts to add from are still loading. */
  loading?: boolean;
  invalid?: boolean;
}

export const RecipientField: React.FC<IRecipientFieldProps> = ({
  label,
  people,
  value,
  onChange,
  disabled,
  placeholder = ADD_PLACEHOLDER,
  noOptionsText = 'No escalation contacts for this organization',
  divider,
  loading,
  invalid,
}) => (
  <FieldRow label={label} divider={divider}>
    <Autocomplete
      multiple
      options={people}
      disabled={disabled}
      value={people.filter((person) => value.includes(person.userId))}
      onChange={(_, next) => onChange(next.map((person) => person.userId))}
      getOptionLabel={fullName}
      isOptionEqualToValue={(option, selected) =>
        option.userId === selected.userId
      }
      noOptionsText={noOptionsText}
      loading={loading}
      loadingText="Loading contacts"
      // Reads as an address line, not a dropdown: the placeholder stays next
      // to the chips so adding someone is always visibly on offer.
      forcePopupIcon={false}
      renderTags={(selected, getTagProps) =>
        selected.map((person, index) => (
          <PersonChip
            {...getTagProps({ index })}
            key={person.userId}
            person={person}
          />
        ))
      }
      renderOption={(props, option) => (
        <li {...props} key={option.userId}>
          <Stack>
            <Typography sx={{ fontSize: '14px', color: '#213B54' }}>
              {fullName(option)}
            </Typography>
            {!!option.email && (
              <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
                {option.email}
              </Typography>
            )}
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          variant="standard"
          placeholder={value.length ? ADD_PLACEHOLDER : placeholder}
          inputProps={{
            ...params.inputProps,
            'aria-label': label,
            'aria-invalid': invalid || undefined,
          }}
          InputProps={{ ...params.InputProps, disableUnderline: true }}
          sx={{
            '& .MuiInputBase-root': { minHeight: '36px', fontSize: '14px' },
          }}
        />
      )}
    />
  </FieldRow>
);
